import { useState } from "react";
import { CardComp } from "../..";
import { INVESTOR_CONTENT_SECTIONS } from "../../../store/admin/investorPortal/constants";

/**
 * Accordion editor mapping admin fields to investor-facing detail sections.
 */
export default function AdminContentSectionEditor({ values, onChange }) {
  const [openKey, setOpenKey] = useState(INVESTOR_CONTENT_SECTIONS[0]?.key);

  const setField = (field, value) => onChange({ ...values, [field]: value });

  const renderField = (field) => {
    switch (field) {
      case "title":
        return (
          <input
            className="form-control investor-portal-note-input"
            value={values.title || ""}
            onChange={(e) => setField("title", e.target.value)}
            placeholder="Hero title"
          />
        );
      case "shortDescription":
        return (
          <textarea
            className="form-control investor-portal-note-input"
            rows={2}
            value={values.shortDescription || ""}
            onChange={(e) => setField("shortDescription", e.target.value)}
            placeholder="Short description shown on hero"
          />
        );
      case "coverImageUrl":
        return (
          <input
            className="form-control investor-portal-note-input"
            value={values.coverImageUrl || ""}
            onChange={(e) => setField("coverImageUrl", e.target.value)}
            placeholder="Cover image URL"
          />
        );
      case "highlights":
        return (
          <textarea
            className="form-control investor-portal-note-input"
            rows={3}
            value={Array.isArray(values.highlights) ? values.highlights.join("\n") : values.highlights || ""}
            onChange={(e) => setField("highlights", e.target.value.split("\n").filter(Boolean))}
            placeholder="One highlight per line"
          />
        );
      case "howItWorks":
        return (
          <textarea
            className="form-control investor-portal-note-input"
            rows={4}
            value={
              Array.isArray(values.howItWorks)
                ? values.howItWorks.map((s) => `${s.step}. ${s.title}: ${s.description}`).join("\n")
                : ""
            }
            onChange={(e) => {
              const steps = e.target.value
                .split("\n")
                .filter(Boolean)
                .map((line, i) => {
                  const parts = line.replace(/^\d+\.\s*/, "").split(":");
                  return {
                    step: i + 1,
                    title: (parts[0] || "").trim(),
                    description: (parts.slice(1).join(":") || "").trim(),
                  };
                });
              setField("howItWorks", steps);
            }}
            placeholder="Step format: 1. Title: Description"
          />
        );
      case "gallery":
        return (
          <textarea
            className="form-control investor-portal-note-input"
            rows={3}
            value={
              Array.isArray(values.gallery)
                ? values.gallery.map((g) => `${g.label}|${g.url}`).join("\n")
                : ""
            }
            onChange={(e) => {
              const gallery = e.target.value
                .split("\n")
                .filter(Boolean)
                .map((line) => {
                  const [label, url] = line.split("|");
                  return { label: (label || "").trim(), url: (url || "").trim() };
                });
              setField("gallery", gallery);
            }}
            placeholder="Label|Image URL — one per line"
          />
        );
      case "milestones":
        return (
          <textarea
            className="form-control investor-portal-note-input"
            rows={3}
            value={
              Array.isArray(values.milestones)
                ? values.milestones.map((m) => `${m.label}|${m.date}|${m.status}`).join("\n")
                : ""
            }
            onChange={(e) => {
              const milestones = e.target.value
                .split("\n")
                .filter(Boolean)
                .map((line) => {
                  const [label, date, status] = line.split("|");
                  return { label: label?.trim(), date: date?.trim(), status: status?.trim() || "upcoming" };
                });
              setField("milestones", milestones);
            }}
            placeholder="Label|Date|status — one per line"
          />
        );
      case "faqs":
        return (
          <textarea
            className="form-control investor-portal-note-input"
            rows={4}
            value={
              Array.isArray(values.faqs)
                ? values.faqs.map((f) => `Q: ${f.question}\nA: ${f.answer}`).join("\n\n")
                : ""
            }
            onChange={(e) => {
              const blocks = e.target.value.split("\n\n").filter(Boolean);
              const faqs = blocks.map((block) => {
                const lines = block.split("\n");
                const q = lines.find((l) => l.startsWith("Q:"))?.replace(/^Q:\s*/, "") || "";
                const a = lines.find((l) => l.startsWith("A:"))?.replace(/^A:\s*/, "") || "";
                return { question: q, answer: a };
              });
              setField("faqs", faqs);
            }}
            placeholder="Q: Question\nA: Answer"
          />
        );
      default:
        return (
          <textarea
            className="form-control investor-portal-note-input"
            rows={3}
            value={values[field] || ""}
            onChange={(e) => setField(field, e.target.value)}
          />
        );
    }
  };

  return (
    <div className="admin-content-section-editor">
      {INVESTOR_CONTENT_SECTIONS.map((section) => {
        const isOpen = openKey === section.key;
        return (
          <CardComp key={section.key} cardClassName="border-0 shadow-sm mb-2 py-0 px-0">
            <button
              type="button"
              className="admin-content-section-toggle w-100 text-start"
              onClick={() => setOpenKey(isOpen ? null : section.key)}
              aria-expanded={isOpen}
            >
              <span className="fw-semibold" style={{ color: "#383838", fontSize: 14 }}>
                {section.label}
              </span>
              <span className="text-muted small ms-2">— maps to investor detail page</span>
            </button>
            {isOpen ? (
              <div className="px-3 pb-3 pt-1 d-flex flex-column gap-2">
                {section.fields.map((field) => (
                  <div key={field}>
                    <label className="form-label small fw-semibold text-muted mb-1">{field}</label>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            ) : null}
          </CardComp>
        );
      })}
    </div>
  );
}
